import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Radio, Settings, Crown, HardDrive, MessageCircle, ShoppingBag,
  BookOpen, Music, Wrench, TrendingUp, Users, LifeBuoy, Mail, Globe, Megaphone, FileText
} from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";

import AdminRequestsTab from "../components/admin/AdminRequestsTab";
import AdminUsersTab from "../components/admin/AdminUsersTab";
import AdminWhiteLabelTab from "../components/admin/AdminWhiteLabelTab";
import AdminTicketsTab from "../components/admin/AdminTicketsTab";
import AdminChatTab from "../components/admin/AdminChatTab";
import AdminLeadsTab from "../components/admin/AdminLeadsTab";
import AdminShopTab from "../components/admin/AdminShopTab";
import AdminResourcesTab from "../components/admin/AdminResourcesTab";
import AdminNetworkTab from "../components/admin/AdminNetworkTab";
import AdminSubmissionsTab from "../components/admin/AdminSubmissionsTab";
import AdminMaintenanceTab from "../components/admin/AdminMaintenanceTab";
import AdminAirplayTab from "../components/admin/AdminAirplayTab";
import AdminSiteSettingsTab from "../components/admin/AdminSiteSettingsTab";
import AdminDomainsTab from "../components/admin/AdminDomainsTab";
import AdminAdBookingsTab from "../components/admin/AdminAdBookingsTab";
import AdminDirectoryTab from "../components/admin/AdminDirectoryTab";
import AdminNewsTab from "../components/admin/AdminNewsTab";
import { EmailTab } from "../components/dashboard/EmailTab";

const AdminPanel = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeAdminTab, setActiveAdminTab] = useState('requests');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updateMsg, setUpdateMsg] = useState('');
  const [showMoreTabs, setShowMoreTabs] = useState(false);
  const [draftCount, setDraftCount] = useState(0);

  const authToken = token || localStorage.getItem('token');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    setLoading(false);
  }, [user, navigate, authLoading]);

  // Poll draft count so the badge reflects new Spotlights / drafts without a full reload.
  useEffect(() => {
    if (!authToken) return;
    let alive = true;
    const fetchDraftCount = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/news?status=draft&limit=200`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (alive && Array.isArray(d)) setDraftCount(d.length);
      } catch { /* silent */ }
    };
    fetchDraftCount();
    const id = setInterval(fetchDraftCount, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [authToken, activeAdminTab]);

  const onMessage = (msg) => {
    setUpdateMsg(msg);
    setTimeout(() => setUpdateMsg(''), 4000);
  };

  if (loading || authLoading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <Radio className="w-12 h-12 text-purple-400 animate-pulse" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-400 text-xl mb-4">{error}</p>
        <button onClick={() => navigate('/')} className="text-purple-400 hover:text-purple-300">Go Home</button>
      </div>
    </div>
  );

  const primaryTabs = [
    { id: 'requests', label: 'Requests', icon: HardDrive },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'whitelabel', label: 'Channels', icon: Crown },
    { id: 'submissions', label: 'Submissions', icon: Music },
    { id: 'airplay', label: 'Airplay', icon: TrendingUp },
    { id: 'ad-bookings', label: 'Ads', icon: Megaphone },
    { id: 'livechat', label: 'Chat', icon: MessageCircle },
  ];

  const moreTabs = [
    { id: 'directory', label: 'Directory', icon: Globe },
    { id: 'news', label: 'News', icon: FileText },
    { id: 'domains', label: 'Domains', icon: Globe },
    { id: 'leads', label: 'Leads', icon: TrendingUp },
    { id: 'tickets', label: 'Tickets', icon: LifeBuoy },
    { id: 'shop', label: 'Shop', icon: ShoppingBag },
    { id: 'resources', label: 'Resources', icon: BookOpen },
    { id: 'network', label: 'Network', icon: Radio },
    { id: 'maintenance', label: 'Maintenance', icon: Wrench },
    { id: 'site-settings', label: 'Settings', icon: Settings },
  ];

  const moreTabIds = moreTabs.map(t => t.id);

  return (
    <div className="min-h-screen bg-gray-950 p-6" data-testid="admin-panel">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src="/logo-header.png" alt="DJsandMCMedia" className="h-8 w-auto" />
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-white/60 hover:text-white transition">
            &larr; Back to Dashboard
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="relative flex items-center gap-1 mb-6 bg-white/5 rounded-xl p-1.5" data-testid="admin-tabs">
          {primaryTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => { setActiveAdminTab(tab.id); setShowMoreTabs(false); }}
                className={`flex items-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeAdminTab === tab.id ? 'bg-purple-500/20 text-purple-400' : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                }`} data-testid={`admin-tab-${tab.id}`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span>{tab.label}</span>
              </button>
            );
          })}

          {/* More Dropdown */}
          <div className="relative ml-auto">
            <button
              onClick={(e) => { e.stopPropagation(); setShowMoreTabs(prev => !prev); }}
              className={`relative flex items-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                moreTabIds.includes(activeAdminTab)
                  ? 'bg-purple-500/20 text-purple-400'
                  : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}
              data-testid="admin-tab-more"
            >
              <Settings className="w-4 h-4 flex-shrink-0" />
              <span>More</span>
              <svg className={`w-3 h-3 transition-transform ${showMoreTabs ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              {draftCount > 0 && activeAdminTab !== 'news' && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-gray-950 animate-pulse"
                  title={`${draftCount} draft${draftCount === 1 ? '' : 's'} waiting for review`}
                  data-testid="more-draft-badge"
                >
                  {draftCount > 9 ? '9+' : draftCount}
                </span>
              )}
            </button>

            {showMoreTabs && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMoreTabs(false)} />
                <div className="absolute right-0 top-full mt-2 bg-gray-900 border border-white/10 rounded-xl py-1.5 w-52 z-50 shadow-2xl shadow-black/50" data-testid="more-dropdown">
                  {moreTabs.map(tab => {
                    const Icon = tab.icon;
                    const showNewsBadge = tab.id === 'news' && draftCount > 0;
                    return (
                      <button key={tab.id} onClick={() => { setActiveAdminTab(tab.id); setShowMoreTabs(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                          activeAdminTab === tab.id ? 'bg-purple-500/20 text-purple-400' : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`} data-testid={`admin-tab-${tab.id}`}>
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span>{tab.label}</span>
                        {showNewsBadge && (
                          <span
                            className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                            title={`${draftCount} draft${draftCount === 1 ? '' : 's'} waiting`}
                            data-testid="news-tab-draft-badge"
                          >
                            {draftCount > 9 ? '9+' : draftCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Status Message */}
        {updateMsg && (
          <div className={`mb-6 p-4 rounded-xl text-sm ${
            updateMsg.includes('approved') || updateMsg.includes('connected') || updateMsg.includes('created') || updateMsg.includes('updated') || updateMsg.includes('activated') || updateMsg.includes('saved') || updateMsg.includes('Saved') || updateMsg.includes('deleted') || updateMsg.includes('Deleted')
              ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
          }`} data-testid="admin-status-msg">
            {updateMsg}
          </div>
        )}

        {/* Tab Content */}
        {activeAdminTab === 'requests' && <AdminRequestsTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'users' && <AdminUsersTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'whitelabel' && <AdminWhiteLabelTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'tickets' && <AdminTicketsTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'livechat' && <AdminChatTab authToken={authToken} />}
        {activeAdminTab === 'leads' && <AdminLeadsTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'shop' && <AdminShopTab authToken={authToken} />}
        {activeAdminTab === 'resources' && <AdminResourcesTab authToken={authToken} />}
        {activeAdminTab === 'network' && <AdminNetworkTab authToken={authToken} />}
        {activeAdminTab === 'submissions' && <AdminSubmissionsTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'maintenance' && <AdminMaintenanceTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'airplay' && <AdminAirplayTab authToken={authToken} />}
        {activeAdminTab === 'site-settings' && <AdminSiteSettingsTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'domains' && <AdminDomainsTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'ad-bookings' && <AdminAdBookingsTab authToken={authToken} onMessage={onMessage} />}
        {activeAdminTab === 'directory' && <AdminDirectoryTab token={authToken} />}
        {activeAdminTab === 'news' && <AdminNewsTab authToken={authToken} onMessage={onMessage} />}
      </div>
    </div>
  );
};

export default AdminPanel;
